// Dash-wide "market details" affordance (Dan, Jul 2026; renamed destination-
// neutral for #248). The pattern: clicking a game/event row or card opens
// the in-page order screen (buy panel) on the right; THIS icon is the
// separate, explicit path to a market's detail surface. Render it only when
// the caller resolved an href — absence means no detail surface exists for
// the market, so the icon doubles as the "a detail view exists" indicator.
//
// The destination itself varies by market: non-tennis hrefs point at the
// /game/... detail page, tennis hrefs point at the focused Value Dashboard
// deep link (/dashboard?focus=...) since there is no tennis /game/... page
// (#248). The component's name and copy stay generic on purpose — it does
// not know or care which shape `href` is.
//
// Same-page navigation on purpose: a popped tab is another live-feed
// consumer (orderbook subscriptions on top of the shared feed —
// backpressure), so the default click stays in-page. Ctrl/Cmd/middle-click
// on the underlying <Link> still opens a tab when the trader really wants
// one.

import { createContext, useContext, type MouseEvent } from 'react';
import { Link } from 'react-router-dom';
import { ChartNoAxesCombined } from 'lucide-react';

/** #1184 — a surface may vet a chip click before the Link navigates: it
 *  returns true when it handled the click itself (Position Tracker opens a
 *  held Kalshi position's account record instead of an empty Game Detail).
 *  Absent — every other surface — the chip behaves exactly as before. The href
 *  is never changed, so modified clicks still reach it. */
export const GameDetailLinkInterceptContext = createContext<
  ((event: MouseEvent<HTMLElement>, href: string) => boolean) | null
>(null);

export function GameDetailLink({
  href,
  size = 12,
  className = '',
}: {
  href: string;
  size?: number;
  className?: string;
}) {
  const intercept = useContext(GameDetailLinkInterceptContext);
  return (
    <Link
      to={href}
      // Hosts are click-to-open-order-panel rows/cards — never let this
      // bubble into that handler.
      onClick={(e: MouseEvent<HTMLElement>) => {
        e.stopPropagation();
        if (intercept?.(e, href)) e.preventDefault();
      }}
      title="Open market details"
      aria-label="Open market details"
      // Deliberately LOUD-ish for the rollout (Dan, Jul 2026): the behavior
      // is new, so the icon rests as a visible blue button chip instead of
      // the ghost-grey hover-reveal it started as. Once the affordance is
      // learned dashboard-wide, calming it back down is this one line
      // (muted-foreground at rest, tint on hover only).
      className={`inline-flex shrink-0 items-center rounded-md border border-blue-500/40 bg-blue-500/10 p-1 align-middle text-blue-400 transition-colors hover:border-blue-400/70 hover:bg-blue-500/25 hover:text-blue-300 ${className}`}
    >
      <ChartNoAxesCombined size={size} />
    </Link>
  );
}
